import { useState, useEffect } from "react";
import BackMethods from "../../back_component/BackMethods";
import { useNavigate, useLocation } from "react-router-dom";
import { can, PERMISSIONS } from "../../auth/permissions";
import PermissionsManagementModals from "./PermissionsManagementModals";

export default function PermissionsManagementPage() {
    const location = useLocation();
    const successMessage = location.state?.successMessage;
    const [deleteSuccessMessage, setDeleteSuccessMessage] = useState("");
    const [flash, setFlash] = useState(null);
    const navigate = useNavigate();
    const { backEnd_request } = BackMethods();

    const [permissions, setPermissions] = useState([]);
    const [filteredPermissions, setFilteredPermissions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [isLoading, setIsLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [modalPermissionId, setModalPermissionId] = useState(null);

    const canCreate = can(PERMISSIONS.PERMISSIONS_CREATE);
    const canDelete = can(PERMISSIONS.PERMISSIONS_DELETE);

    const fetchPermissions = async () => {
        setIsLoading(true);
        try {
            const res = await backEnd_request.get("/admin/Permissions");
            const data = res.data.permissions || res.data || [];
            setPermissions(data);
            setFilteredPermissions(data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchPermissions(); }, []);

    useEffect(() => {
        if (successMessage) {
            setFlash({ message: successMessage, variant: "success" });
            navigate(".", { replace: true, state: {} });
        }
    }, [successMessage, navigate]);

    useEffect(() => {
        if (!flash?.message && !deleteSuccessMessage) return undefined;
        const t = setTimeout(() => {
            setFlash(null);
            setDeleteSuccessMessage("");
        }, 3500);
        return () => clearTimeout(t);
    }, [flash, deleteSuccessMessage]);

    const handleSearch = (term, type) => {
        setSearchTerm(term);
        setFilterType(type);
        let filtered = permissions.filter((perm) => {
            const val = term.toLowerCase();
            if (type === "name") return perm.name?.toLowerCase().includes(val);
            if (type === "displayName") return perm.displayName?.toLowerCase().includes(val);
            if (type === "category") return perm.category?.toLowerCase().includes(val);
            return (
                perm.name?.toLowerCase().includes(val) ||
                perm.displayName?.toLowerCase().includes(val) ||
                perm.category?.toLowerCase().includes(val)
            );
        });
        setFilteredPermissions(filtered);
    };

    const handleDelete = async (permissionId) => {
        if (!window.confirm("هل أنت متأكد من حذف هذه الصلاحية؟")) return;
        try {
            await backEnd_request.delete(`/admin/Permissions/${permissionId}`);
            fetchPermissions();
            setDeleteSuccessMessage("تم حذف الصلاحية بنجاح");
        } catch (error) {
            console.error(error);
        }
    };

    const closeModal = () => {
        setModal(null);
        setModalPermissionId(null);
    };

    const onModalSaved = ({ message } = {}) => {
        fetchPermissions();
        setFlash({ message: message || "تم حفظ الصلاحية بنجاح", variant: "success" });
    };

    return (
        <div className="permissions-page-minimal" style={{ direction: 'rtl', fontFamily: 'Cairo, sans-serif' }}>
            <div className="container py-5 px-3">

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-5 gap-3">
                    <div>
                        <h2 className="fw-bold text-dark m-0">إدارة الصلاحيات</h2>
                        <p className="text-muted small mb-0 mt-1">التحكم في وصول المستخدمين لوظائف النظام الأساسية</p>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {canCreate && (
                            <button
                                type="button"
                                className="btn btn-dark rounded-3 px-4 py-2 fw-medium shadow-sm"
                                onClick={() => setModal("create")}
                            >
                                <i className="bi bi-shield-plus ms-2"></i> إضافة صلاحية
                            </button>
                        )}
                    </div>
                </div>

                {(flash?.message || deleteSuccessMessage) && (
                    <div className="alert alert-success py-2 px-3 mb-3" role="alert">
                        {flash?.message || deleteSuccessMessage}
                    </div>
                )}

                <div className="bg-white border rounded-4 p-2 mb-4 shadow-sm search-card">
                    <div className="row g-2">
                        <div className="col-md-9 border-end-md">
                            <div className="input-group">
                                <span className="input-group-text bg-transparent border-0 text-muted"><i className="bi bi-search"></i></span>
                                <input type="text" className="form-control border-0 shadow-none" placeholder="ابحث عن اسم الصلاحية أو الفئة..." value={searchTerm} onChange={(e) => handleSearch(e.target.value, filterType)} />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <select className="form-select border-0 shadow-none fw-medium" value={filterType} onChange={(e) => handleSearch(searchTerm, e.target.value)}>
                                <option value="all">بحث شامل</option>
                                <option value="name">الاسم التقني</option>
                                <option value="displayName">الاسم الظاهر</option>
                                <option value="category">الفئة</option>
                            </select>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="text-center py-5"><div className="spinner-border spinner-border-sm text-secondary"></div></div>
                ) : (
                    <div className="permissions-stack">
                        {filteredPermissions.length > 0 ? (
                            filteredPermissions.map((perm) => (
                                <div key={perm.permissionId} className="perm-card bg-white border rounded-4 mb-3 transition-all">
                                    <div className="p-4">
                                        <div className="row align-items-center g-4">
                                            <div className="col-lg-8">
                                                <div className="d-flex align-items-start gap-3">
                                                    <div className={`perm-icon-box ${perm.isSystemPermission ? 'sys' : 'usr'}`}>
                                                        <i className={`bi ${perm.isSystemPermission ? 'bi-lock-fill' : 'bi-key'}`}></i>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                                            <h5 className="fw-bold m-0 text-dark">{perm.displayName}</h5>
                                                            <span className="category-tag">{perm.category || "عام"}</span>
                                                        </div>
                                                        <div className="mt-2 text-muted fw-mono" style={{ fontSize: '14px', opacity: 0.7 }}>
                                                            {perm.name}
                                                        </div>
                                                        <p className="text-muted small mb-0 mt-2 perm-description">
                                                            {perm.description || "لا يوجد وصف لهذه الصلاحية حالياً."}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-lg-4">
                                                <div className="d-flex flex-column flex-md-row align-items-center justify-content-lg-end gap-4">
                                                    <div className="info-badge">
                                                        <label className="d-block text-muted tiny-label">النوع</label>
                                                        <span className={`fw-bold small ${perm.isSystemPermission ? 'text-warning-emphasis' : 'text-success'}`}>
                                                            {perm.isSystemPermission ? 'نظام' : 'عادي'}
                                                        </span>
                                                    </div>

                                                    {canDelete && !perm.isSystemPermission && (
                                                        <div className="actions-cluster">
                                                            <button
                                                                type="button"
                                                                className="btn-action-minimal text-danger"
                                                                title="حذف"
                                                                onClick={() => handleDelete(perm.permissionId)}
                                                            >
                                                                <i className="bi bi-trash3"></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-5 bg-light rounded-4 border-dashed">
                                <i className="bi bi-shield-slash text-muted fs-2"></i>
                                <p className="text-muted mt-2">لا توجد صلاحيات تطابق البحث</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <PermissionsManagementModals
                modal={modal}
                permissionId={modalPermissionId}
                onClose={closeModal}
                onSaved={onModalSaved}
                canCreate={canCreate}
            />

            <style>{`
                .permissions-page-minimal { background-color: #f8f9fa; min-height: 100vh; }
                
                .perm-card { border-color: #eaecf0 !important; }
                .perm-card:hover { border-color: #d0d5dd !important; box-shadow: 0 10px 15px rgba(0,0,0,0.02); }

                .perm-icon-box { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
                .perm-icon-box.sys { background: #fffcf0; color: #b54708; border: 1px solid #fedf89; }
                .perm-icon-box.usr { background: #f5faff; color: #004eeb; border: 1px solid #d1e9ff; }

                .category-tag { background: #f2f4f7; color: #344054; font-size: 10px; font-weight: 700; padding: 2px 10px; border-radius: 20px; border: 1px solid #eaecf0; }
                .perm-description { line-height: 1.5; max-width: 600px; color: #475467 !important; }
                
                .tiny-label { font-size: 9px; font-weight: 800; text-transform: uppercase; color: #98a2b3; margin-bottom: 0px; }
                
                .btn-action-minimal { width: 40px; height: 40px; border-radius: 50%; border: 1px solid #eaecf0; background: white; transition: 0.2s; display: flex; align-items: center; justify-content: center; }
                .btn-action-minimal:hover { background: #fff1f0; border-color: #fecdca; transform: scale(1.05); }

                @media (min-width: 768px) { .border-end-md { border-left: 1px solid #eaecf0; } }
                @media (max-width: 991px) {
                    .perm-card { text-align: center; }
                    .d-flex.align-items-start { flex-direction: column; align-items: center !important; }
                    .actions-cluster { width: 100%; justify-content: center; }
                    .btn-action-minimal { width: 100%; border-radius: 10px; }
                }
            `}</style>
        </div>
    );
}
